import {
  ORDER_FEATURES,
  ORDER_FEATURE_CATEGORIES,
  OrderFeature,
  OrderFeatureCategory,
  compareOrderFeaturesByCategory,
  noop,
} from '@aloft/toolkit-shared';
import { Button, Checkbox, Flex, Typography } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/prefer-sound-ui-box, aloft/no-sui-chakra, aloft/prefer-sound-ui-button -- no first-class replacement for a grouped checkbox dropdown menu yet; matches AddCompButtons.tsx
import { Box, Menu, MenuButton, MenuItem, MenuList, Portal } from '@aloft/toolkit-sui/chakra';

import { useEffect, useState } from 'react';

import { DISCLOSABLE_FEATURES } from '../../../redux/constants.ts';
import { selectIncludeCostApproach } from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { borderColor, borderStyle, borderWidth } from '../../../styles/border.ts';

type EditFeaturesMenuProps = {
  /** Currently added features. */
  features: OrderFeature[];
  label: string;
  /** Called once, with the full new set of features, when Save is clicked. */
  onSave: (features: OrderFeature[]) => void;
};

/**
 * Groups the given features by category (per {@link ORDER_FEATURES}), preserving
 * the within-category display order.
 */
const groupFeaturesByCategory = (
  editableFeatures: OrderFeature[],
): { category: OrderFeatureCategory; features: OrderFeature[] }[] => {
  const sorted = [...editableFeatures].sort(compareOrderFeaturesByCategory);
  const groups: { category: OrderFeatureCategory; features: OrderFeature[] }[] = [];

  sorted.forEach((feature) => {
    const { category } = ORDER_FEATURES[feature];
    const lastGroup = groups.at(-1);
    if (lastGroup?.category === category) {
      lastGroup.features.push(feature);
    } else {
      groups.push({ category, features: [feature] });
    }
  });

  return groups;
};

export const EditFeaturesMenu = ({ features, label, onSave }: EditFeaturesMenuProps) => {
  const includeCostApproach = useAppSelector(selectIncludeCostApproach);

  const editableFeatures = DISCLOSABLE_FEATURES.filter(
    (feature) => includeCostApproach || feature !== 'manualCost',
  );
  const groups = groupFeaturesByCategory(editableFeatures);

  const [pendingFeatures, setPendingFeatures] = useState<OrderFeature[]>(features);

  useEffect(() => {
    setPendingFeatures((current) => {
      if (
        current.length === features.length &&
        features.every((feature) => current.includes(feature))
      ) {
        return current;
      }
      return features;
    });
  }, [features]);

  const toggleFeature = (feature: OrderFeature) => {
    setPendingFeatures((current) =>
      current.includes(feature) ? current.filter((f) => f !== feature) : [...current, feature],
    );
  };

  return (
    <Menu closeOnSelect={false} placement="bottom-end">
      {({ onClose }) => (
        <>
          <MenuButton as={Box} style={{ cursor: 'pointer' }}>
            <Button variant="secondary" size="sm" text={label} onClick={noop} />
          </MenuButton>
          <Portal>
            <MenuList
              minWidth="200px"
              padding="8px"
              zIndex={9999}
              borderColor={borderColor}
              borderStyle={borderStyle}
              borderWidth={borderWidth}
              boxShadow="md"
            >
              <Flex flexDirection="column" gap="8px" maxHeight="50vh">
                <Flex
                  flexDirection="column"
                  gap="8px"
                  overflowY="auto"
                  paddingRight="4px"
                  flex={1}
                  minHeight={0}
                >
                  {groups.map(({ category, features: categoryFeatures }) => (
                    <Flex key={category} flexDirection="column" gap="4px">
                      <Typography variant="subtitle1" color="textTertiary" casing="uppercase">
                        {ORDER_FEATURE_CATEGORIES[category].label}
                      </Typography>
                      {categoryFeatures.map((feature) => (
                        <MenuItem
                          key={feature}
                          onClick={() => toggleFeature(feature)}
                          padding="4px 8px"
                          fontSize="0.875rem"
                        >
                          <Flex gap="8px" alignItems="center">
                            <Box pointerEvents="none">
                              <Checkbox isChecked={pendingFeatures.includes(feature)} />
                            </Box>
                            <Typography variant="body2">{ORDER_FEATURES[feature].label}</Typography>
                          </Flex>
                        </MenuItem>
                      ))}
                    </Flex>
                  ))}
                </Flex>
                <Button
                  variant="primary"
                  size="sm"
                  fullWidth
                  text="Save"
                  onClick={() => {
                    onSave(pendingFeatures);
                    onClose();
                  }}
                />
              </Flex>
            </MenuList>
          </Portal>
        </>
      )}
    </Menu>
  );
};
